// /opt/isp_erp/frontend/src/components/warehouse/DevolucionesCentralPanel.jsx
import React, { useState, useEffect } from "react";
import {
  Box, Typography, Paper, Table, TableHead, TableRow, TableCell, TableBody, TableContainer, TablePagination,
  Button, TextField, MenuItem, Alert, Chip, Dialog, DialogTitle, DialogContent, DialogActions, Autocomplete,
  CircularProgress, Grid, Checkbox,
} from "@mui/material";
import axios from "../../api/axios";
import { mensajeError } from "../../utils/mensajesError";

const API = "/warehouse/sucursales-equipos/devoluciones";
const fmtFecha = (v) => (v ? new Date(v).toLocaleString("es-EC") : "—");
const ESTADO_COLOR = { EN_TRANSITO: "warning", RECIBIDO: "success", ANULADO: "default" };
const ESTADO_TEXTO = { EN_TRANSITO: "En tránsito", RECIBIDO: "Recibida", ANULADO: "Anulada" };
const TIPO_TEXTO = { MATERIAL: "Material", EQUIPO: "Equipo", VINETA: "Viñeta" };

function descargar(datos, nombre) {
  const url = window.URL.createObjectURL(new Blob([datos], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = nombre;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

function NuevaDevolucionDialog({ ctx, onClose, onHecho }) {
  const operables = (ctx.sucursales || []).filter((s) => s.puede_operar);
  const [sucursalId, setSucursalId] = useState(operables.length === 1 ? operables[0].id : "");
  const [stock, setStock] = useState([]);
  const [equiposOp, setEquiposOp] = useState([]);
  const [vinetasOp, setVinetasOp] = useState([]);
  const [lineas, setLineas] = useState([]);
  const [equipos, setEquipos] = useState([]);
  const [vinetas, setVinetas] = useState([]);
  const [entrega, setEntrega] = useState("");
  const [obs, setObs] = useState("");
  const [error, setError] = useState("");
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    if (!sucursalId) return;
    setLineas([]); setEquipos([]); setVinetas([]);
    const p = { params: { page: 1, page_size: 100 } };
    axios.get(`/warehouse/sucursales/${sucursalId}/stock`, p).then((r) => setStock((r.data.items || []).filter((m) => m.cantidad > 0))).catch(() => setStock([]));
    axios.get(`/warehouse/sucursales-equipos/${sucursalId}/equipos`, p).then((r) => setEquiposOp((r.data.items || []).filter((e) => !e.en_revision))).catch(() => setEquiposOp([]));
    axios.get(`/warehouse/sucursales-equipos/${sucursalId}/vinetas`, p).then((r) => setVinetasOp(r.data.items || [])).catch(() => setVinetasOp([]));
  }, [sucursalId]);

  const agregarMaterial = (m) => {
    if (!m) return;
    setLineas((prev) => (prev.some((l) => l.m.material_id === m.material_id) ? prev : [...prev, { m, cantidad: "" }]));
  };

  const enviar = async () => {
    setError("");
    if (!sucursalId) { setError("Elige la sucursal"); return; }
    const mats = lineas.filter((l) => Number(l.cantidad) > 0);
    if (lineas.length !== mats.length) { setError("Todas las líneas de material necesitan una cantidad mayor a 0"); return; }
    for (const l of mats) {
      if (Number(l.cantidad) > l.m.cantidad) { setError(`Solo hay ${l.m.cantidad} de ${l.m.nombre}`); return; }
    }
    if (mats.length === 0 && equipos.length === 0 && vinetas.length === 0) { setError("Agrega al menos un material, equipo o viñeta"); return; }
    setEnviando(true);
    try {
      const r = await axios.post(API, {
        sucursal_id: Number(sucursalId),
        materiales: mats.map((l) => ({ material_id: l.m.material_id, cantidad: Number(l.cantidad) })),
        equipos: equipos.map((e) => e.sn),
        vinetas: vinetas.map((v) => v.id),
        entrega_nombre: entrega.trim() || null,
        observaciones: obs.trim() || null,
      });
      onHecho(r.data);
    } catch (e) {
      setError(mensajeError(e, "No se pudo registrar la devolución"));
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Dialog open onClose={enviando ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>Nueva devolución a la bodega central</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12} md={6}>
            <TextField select fullWidth size="small" label="Sucursal que devuelve" value={sucursalId} onChange={(e) => setSucursalId(e.target.value)}>
              {operables.map((s) => (<MenuItem key={s.id} value={s.id}>{s.nombre}</MenuItem>))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth size="small" label="Quién entrega (opcional)" value={entrega} onChange={(e) => setEntrega(e.target.value)} />
          </Grid>
          <Grid item xs={12}><Typography variant="subtitle2">Materiales</Typography></Grid>
          <Grid item xs={12}>
            <Autocomplete options={stock} value={null} blurOnSelect clearOnBlur
              getOptionLabel={(o) => `${o.codigo} - ${o.nombre} (hay ${o.cantidad} ${o.unidad})`}
              onChange={(_, v) => agregarMaterial(v)} noOptionsText="Sin material en la sucursal"
              renderInput={(params) => (<TextField {...params} size="small" label="Agregar material" />)} />
          </Grid>
          {lineas.map((l) => (
            <Grid item xs={12} key={l.m.material_id} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <Typography variant="body2" sx={{ flex: 1 }}>{l.m.nombre} <em>(hay {l.m.cantidad} {l.m.unidad})</em></Typography>
              <TextField size="small" type="number" label="Cantidad" value={l.cantidad} sx={{ width: 130 }}
                onChange={(e) => setLineas((prev) => prev.map((x) => (x.m.material_id === l.m.material_id ? { ...x, cantidad: e.target.value } : x)))} />
              <Button size="small" color="warning" onClick={() => setLineas((prev) => prev.filter((x) => x.m.material_id !== l.m.material_id))}>Quitar</Button>
            </Grid>
          ))}
          <Grid item xs={12}><Typography variant="subtitle2">Equipos (los que están en revisión no se pueden devolver por aquí)</Typography></Grid>
          <Grid item xs={12}>
            <Autocomplete multiple disableCloseOnSelect options={equiposOp} value={equipos} filterSelectedOptions limitTags={4}
              getOptionLabel={(o) => `${o.sn} · ${o.modelo}`} isOptionEqualToValue={(a, b) => a.sn === b.sn}
              onChange={(_, v) => setEquipos(v)} noOptionsText="Sin equipos disponibles"
              renderInput={(params) => (<TextField {...params} size="small" label="Equipos" />)} />
          </Grid>
          <Grid item xs={12}><Typography variant="subtitle2">Viñetas</Typography></Grid>
          <Grid item xs={12}>
            <Autocomplete multiple options={vinetasOp} value={vinetas} filterSelectedOptions
              getOptionLabel={(o) => `${o.serial} - ${o.nombre}`} isOptionEqualToValue={(a, b) => a.id === b.id}
              onChange={(_, v) => setVinetas(v)} noOptionsText="Sin viñetas disponibles"
              renderInput={(params) => (<TextField {...params} size="small" label="Viñetas" />)} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth size="small" label="Observaciones (opcional)" value={obs} onChange={(e) => setObs(e.target.value)} />
          </Grid>
        </Grid>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={enviando}>Cancelar</Button>
        <Button variant="contained" onClick={enviar} disabled={enviando}>{enviando ? "Enviando..." : "Registrar devolución"}</Button>
      </DialogActions>
    </Dialog>
  );
}

function DetalleDevolucionDialog({ ctx, devId, onClose, onCambio }) {
  const [d, setD] = useState(null);
  const [error, setError] = useState("");
  const [marcados, setMarcados] = useState({});
  const [cant, setCant] = useState({});
  const [recibe, setRecibe] = useState("");
  const [obs, setObs] = useState("");
  const [motivo, setMotivo] = useState("");
  const [anulando, setAnulando] = useState(false);
  const [trabajando, setTrabajando] = useState(false);

  useEffect(() => {
    let vivo = true;
    axios.get(`${API}/${devId}`).then((r) => {
      if (!vivo) return;
      setD(r.data);
      const m = {}; const c = {};
      r.data.items.forEach((i) => { m[i.id] = true; c[i.id] = i.cantidad_enviada; });
      setMarcados(m); setCant(c);
    }).catch((e) => { if (vivo) setError(mensajeError(e, "No se pudo cargar la devolución")); });
    return () => { vivo = false; };
  }, [devId]);

  if (!d) {
    return (
      <Dialog open onClose={onClose} fullWidth maxWidth="sm">
        <DialogContent sx={{ textAlign: "center", py: 4 }}>{error ? <Alert severity="error">{error}</Alert> : <CircularProgress />}</DialogContent>
        <DialogActions><Button onClick={onClose}>Cerrar</Button></DialogActions>
      </Dialog>
    );
  }

  const suc = (ctx.sucursales || []).find((s) => s.id === d.sucursal_id);
  const enTransito = d.estado === "EN_TRANSITO";
  const puedeConfirmar = enTransito && ctx.central !== false;
  const puedeAnular = enTransito && !!(suc && suc.puede_operar);

  const descargarActa = async () => {
    try {
      const r = await axios.get(`${API}/${d.id}/pdf`, { responseType: "blob" });
      descargar(r.data, `${d.numero}.pdf`);
    } catch (e) { setError("No se pudo descargar el acta"); }
  };

  const confirmar = async () => {
    setError("");
    setTrabajando(true);
    try {
      const recibidos = d.items.filter((i) => marcados[i.id]).map((i) =>
        (i.tipo_item === "MATERIAL" ? { id: i.id, cantidad: Number(cant[i.id]) } : { id: i.id }));
      const r = await axios.post(`${API}/${d.id}/confirmar`, { recibidos, recibe_nombre: recibe.trim() || null, observaciones: obs.trim() || null });
      setD(r.data);
      onCambio("Devolución confirmada");
    } catch (e) { setError(mensajeError(e, "No se pudo confirmar la devolución")); } finally { setTrabajando(false); }
  };

  const anular = async () => {
    setError("");
    if (motivo.trim().length < 3) { setError("Indica el motivo de la anulación"); return; }
    setTrabajando(true);
    try {
      const r = await axios.post(`${API}/${d.id}/anular`, { motivo: motivo.trim() });
      setD(r.data); setAnulando(false);
      onCambio("Devolución anulada: todo volvió a la sucursal");
    } catch (e) { setError(mensajeError(e, "No se pudo anular")); } finally { setTrabajando(false); }
  };

  return (
    <Dialog open onClose={trabajando ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>{d.numero} <Chip size="small" sx={{ ml: 1 }} color={ESTADO_COLOR[d.estado]} label={ESTADO_TEXTO[d.estado] || d.estado} /></DialogTitle>
      <DialogContent>
        <Typography variant="body2">Origen: <strong>{d.sucursal_nombre}</strong> · {d.n_materiales} material(es) · {d.n_equipos} equipo(s) · {d.n_vinetas} viñeta(s)</Typography>
        <Typography variant="body2">Enviada el {fmtFecha(d.fecha_envio)} por {d.enviado_por || "—"} · entrega: {d.entrega_nombre || "—"}</Typography>
        {d.observaciones && <Typography variant="body2">Observaciones: {d.observaciones}</Typography>}
        {d.estado === "RECIBIDO" && <Typography variant="body2">Recibida el {fmtFecha(d.recibido_at)} por {d.recibido_por || "—"} · recibe: {d.recibe_nombre || "—"}</Typography>}
        {d.observaciones_recepcion && <Typography variant="body2">Observaciones de la recepción: {d.observaciones_recepcion}</Typography>}
        {d.estado === "ANULADO" && <Alert severity="warning" sx={{ mt: 1 }}>Anulada el {fmtFecha(d.anulado_at)}: {d.motivo_anulacion}</Alert>}
        <TableContainer sx={{ mt: 2, maxHeight: 360 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {puedeConfirmar && <TableCell padding="checkbox" />}
                <TableCell>Tipo</TableCell><TableCell>Referencia</TableCell><TableCell>Descripción</TableCell>
                <TableCell align="right">Enviado</TableCell>
                <TableCell align="right">{puedeConfirmar ? "Recibido" : "Recibido"}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {d.items.map((i) => (
                <TableRow key={i.id}>
                  {puedeConfirmar && (
                    <TableCell padding="checkbox">
                      <Checkbox size="small" checked={!!marcados[i.id]} onChange={(e) => setMarcados((p) => ({ ...p, [i.id]: e.target.checked }))} />
                    </TableCell>
                  )}
                  <TableCell>{TIPO_TEXTO[i.tipo_item]}</TableCell>
                  <TableCell>{i.referencia}</TableCell>
                  <TableCell>{i.descripcion}</TableCell>
                  <TableCell align="right">{i.tipo_item === "MATERIAL" ? `${i.cantidad_enviada} ${i.unidad || ""}` : 1}</TableCell>
                  <TableCell align="right">
                    {puedeConfirmar && i.tipo_item === "MATERIAL" ? (
                      <TextField size="small" type="number" sx={{ width: 100 }} disabled={!marcados[i.id]} value={cant[i.id] ?? ""}
                        onChange={(e) => setCant((p) => ({ ...p, [i.id]: e.target.value }))} />
                    ) : puedeConfirmar ? "" : (i.tipo_item === "MATERIAL" ? (i.cantidad_recibida ?? "—") : (i.recibido === null ? "—" : i.recibido ? "Sí" : "No"))}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {puedeConfirmar && (
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={4}><TextField fullWidth size="small" label="Quién recibe (opcional)" value={recibe} onChange={(e) => setRecibe(e.target.value)} /></Grid>
            <Grid item xs={12} md={8}><TextField fullWidth size="small" label="Observaciones (obligatorias si falta algo)" value={obs} onChange={(e) => setObs(e.target.value)} /></Grid>
          </Grid>
        )}
        {anulando && <TextField fullWidth size="small" label="Motivo de la anulación" value={motivo} onChange={(e) => setMotivo(e.target.value)} sx={{ mt: 2 }} />}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={descargarActa}>Descargar acta (PDF)</Button>
        {puedeAnular && !anulando && <Button color="warning" onClick={() => setAnulando(true)}>Anular</Button>}
        {puedeAnular && anulando && <Button color="warning" variant="contained" onClick={anular} disabled={trabajando}>Confirmar anulación</Button>}
        {puedeConfirmar && <Button variant="contained" onClick={confirmar} disabled={trabajando}>{trabajando ? "Guardando..." : "Confirmar recepción"}</Button>}
        <Button onClick={onClose} disabled={trabajando}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function DevolucionesCentralPanel({ ctx }) {
  const [estado, setEstado] = useState("");
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState(25);
  const [data, setData] = useState({ items: [], total: 0 });
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState("");
  const [aviso, setAviso] = useState("");
  const [version, setVersion] = useState(0);
  const [nueva, setNueva] = useState(false);
  const [abierta, setAbierta] = useState(null);
  const puedeCrear = (ctx.sucursales || []).some((s) => s.puede_operar);

  useEffect(() => { setPage(0); }, [estado]);
  useEffect(() => {
    let vivo = true;
    setCargando(true); setError("");
    axios.get(API, { params: { estado: estado || undefined, page: page + 1, page_size: rows } })
      .then((r) => { if (vivo) setData(r.data); })
      .catch((e) => { if (vivo) { setData({ items: [], total: 0 }); setError(mensajeError(e, "No se pudieron cargar las devoluciones")); } })
      .finally(() => { if (vivo) setCargando(false); });
    return () => { vivo = false; };
  }, [estado, page, rows, version]);

  return (
    <Box>
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2, alignItems: "center" }}>
        <TextField select size="small" label="Estado" value={estado} onChange={(e) => setEstado(e.target.value)} sx={{ minWidth: 160 }}>
          <MenuItem value="">Todas</MenuItem>
          <MenuItem value="EN_TRANSITO">En tránsito</MenuItem>
          <MenuItem value="RECIBIDO">Recibidas</MenuItem>
          <MenuItem value="ANULADO">Anuladas</MenuItem>
        </TextField>
        {puedeCrear && <Button variant="contained" onClick={() => setNueva(true)}>Nueva devolución</Button>}
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {aviso && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setAviso("")}>{aviso}</Alert>}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Acta</TableCell><TableCell>Origen</TableCell><TableCell>Fecha</TableCell>
              <TableCell align="right">Materiales</TableCell><TableCell align="right">Equipos</TableCell>
              <TableCell align="right">Viñetas</TableCell><TableCell>Estado</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {cargando && (<TableRow><TableCell colSpan={7} align="center"><CircularProgress size={20} /></TableCell></TableRow>)}
            {!cargando && data.items.length === 0 && (<TableRow><TableCell colSpan={7} align="center">No hay devoluciones a la central</TableCell></TableRow>)}
            {!cargando && data.items.map((x) => (
              <TableRow key={x.id} hover sx={{ cursor: "pointer" }} onClick={() => setAbierta(x.id)}>
                <TableCell>{x.numero}</TableCell><TableCell>{x.sucursal_nombre}</TableCell><TableCell>{fmtFecha(x.fecha_envio)}</TableCell>
                <TableCell align="right">{x.n_materiales}</TableCell><TableCell align="right">{x.n_equipos}</TableCell><TableCell align="right">{x.n_vinetas}</TableCell>
                <TableCell>
                  <Chip size="small" color={ESTADO_COLOR[x.estado]} label={ESTADO_TEXTO[x.estado] || x.estado} />
                  {x.con_diferencia && <Chip size="small" color="error" variant="outlined" label="Con diferencia" sx={{ ml: 1 }} />}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination component="div" count={data.total} page={page} onPageChange={(e, p) => setPage(p)}
          rowsPerPage={rows} onRowsPerPageChange={(e) => { setRows(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50]} labelRowsPerPage="Filas" />
      </TableContainer>
      {nueva && <NuevaDevolucionDialog ctx={ctx} onClose={() => setNueva(false)}
        onHecho={(d) => { setNueva(false); setAviso(`Devolución ${d.numero} registrada`); setVersion((v) => v + 1); setAbierta(d.id); }} />}
      {abierta && <DetalleDevolucionDialog ctx={ctx} devId={abierta} onClose={() => setAbierta(null)}
        onCambio={(m) => { setAviso(m); setVersion((v) => v + 1); }} />}
    </Box>
  );
}
